import React from 'react'
import Head from 'next/head'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import Item, { ItemText, ItemUl } from 'library/molecules/Item'
import { H5, H4, H6, Text } from 'library/atoms/Typography'
import HeroSection, {
  HeroSectionContainer,
  HeroSectionContainerRow
} from 'library/molecules/HeroSection'
import BottomBar from 'library/atoms/BottomBar'
import Price, { PriceWrapper, PriceText } from 'shared/library/atoms/Price'
import { OutletPresenter } from 'shared/presenters/OutletPresenter'
import { SchedulablePresenter } from 'shared/presenters/SchedulablePresenter'
import { useRouter } from 'next/router'
import Image from 'next/image'
import { definedNotNull, defined } from 'shared/presenters/presenter'
import Header from 'library/molecules/Header'
import Layout from 'library/atoms/Layout'
import { BackBreadcrumb } from 'library/molecules/Breadcrumb'
import Icon from 'shared/library/atoms/Icon'
import useTranslation from 'next-translate/useTranslation'
import widgetRoutes from 'enums/routes'
import CoverImage from 'library/atoms/CoverImage'
import Tag, { TagsList } from 'library/atoms/Tag/Tag'
import DetailsList, { DetailsListItem } from 'library/molecules/DetailsList'
import ModalsSchedulableDetails, {
  MODAL_PROPS,
  MODAL_CONFIG,
  MODAL_IDS
} from 'library/molecules/ModalsSchedulableDetails/ModalsSchedulableDetails'
import useModal from 'shared/state/useModal'
import useBasketContainsMembership from 'state/useBasketContainsMembership'
import useBookOnClick from 'state/useBookOnClick'
import { MODAL_PROP_VARIATIONS_AUTH } from 'library/molecules/ModalsAuth'
import useBasket from 'state/useBasket'
import { gql, useQuery } from '@apollo/client'
import { ON_SERVER } from 'shared/enums/environment'
import VoucherBar from 'library/molecules/VoucherBar'
import PoweredByEola from 'library/atoms/PoweredByEola'
import { determineHostname } from 'shared/helpers/determineHostname'
import { ShowOnMarketplace } from 'shared/library/atoms/ConditionalMarketplace'
import {
  BottomBarButton,
  BottomBarContainer
} from 'library/atoms/BottomBar/BottomBar'
import Flex from 'shared/library/atoms/Flex'
import Link from 'next/link'

import Rating from 'library/molecules/Rating'
import ReviewsCarousel from 'library/organisms/ReviewsCarousel'

interface ActivityDetailsProps {
  activity: SchedulablePresenter
  outlet: OutletPresenter
  goToConfig: () => void
  goToRequest: () => void
  goToCancellationPolicy: () => void
  pageTitle?: string
  view?: string
}

const ActivityDetails = ({
  activity,
  outlet,
  goToConfig,
  goToRequest,
  goToCancellationPolicy
}: ActivityDetailsProps) => {
  const { query, push } = useRouter()
  const { widgetSlug, categorySlug } = query as {
    [keys in 'widgetSlug' | 'categorySlug']: string
  }
  const { t } = useTranslation('chargeable')
  const openMembershipModal = useBasketContainsMembership({ widgetSlug })
  const { basket, refetchBasket } = useBasket()

  const basketPriceModifiers = basket?.baskets_price_modifiers?.[0]
  const creditVoucher = basketPriceModifiers?.credit_voucher
  const discountCode = basketPriceModifiers?.discount_code
  const noCancellationPolicy = outlet?.enthusiast_cancellation_policy === 0

  const bookingFromActivityVoucher =
    basket?.getLineItemBookingFromActivityVoucher()

  const activityVoucherLineItemId = basket?.getActivityVoucherLineItemId()

  const code = basket?.getActivityVoucherCode()

  const { activeModal, setActiveModalId, currentModalProps } =
    useModal<MODAL_PROPS>({
      MODAL_CONFIG,
      modalPropVariations: {
        activeVoucher: {
          lineItemId: activityVoucherLineItemId,
          activityVoucherCode: code,
          basketPriceModifier: basketPriceModifiers ?? {},
          basketId: basket?.id,
          refetchBasket,
          onClose: () => {
            setActiveModalId(null)
          },
          changeViewOnSuccess: () => {
            setActiveModalId(null)
          }
        },
        removeMembership: {
          continueAddToBasket: () => {
            refetchBasket({ basketId: null })
            setActiveModalId(null)
            goToConfig()
          },
          closeModal: () => setActiveModalId(null)
        },
        ...MODAL_PROP_VARIATIONS_AUTH
      }
    })

  const bookSchedulableOnClick = useBookOnClick({
    schedulable: activity,
    shoppingBasketDisabled: !outlet?.feature_toggles?.shopping_basket_enabled,
    openMembershipModal,
    goToConfig,
    setActiveModalId
  })

  const { data, loading } = useQuery(FETCH_CALENDAR_AVAILABILITY_AGGREGATE, {
    variables: {
      schedulableId: activity.id
    },
    // No need to fetch availability if we wont show request button anyway
    skip: ON_SERVER || !outlet.enable_requests_for_time_slots,
    ssr: ON_SERVER
  })

  const requestsEnabledAndNoAvailability =
    data?.materialized_bookability_by_days_aggregate?.aggregate?.count == 0

  const prefixHref = determineHostname()

  const numberOfReviews = outlet?.ratings?.[0]?.number_of_ratings || 0

  return (
    <>
      <Head>
        <title>{`${t('common:book')} - ${activity.name()} | eola`}</title>
        <meta
          property="og:title"
          content={`${t('common:book')} - ${activity.name()} | eola`}
        />
        <meta property="og:type" content="WebSite" />
        <meta
          property="og:url"
          content={`${prefixHref}${widgetRoutes.ACTIVITY.url({
            widgetSlug,
            categorySlug: categorySlug,
            activitySlug: definedNotNull(activity.slug)
          })}`}
        />
        <meta property="og:image" content={activity.imageURL('fullscreen')} />
        <meta name="description" content={activity.tagline()} />

        {!outlet.feature_toggles?.marketplace_enabled && (
          <meta name="robots" content="noindex" />
        )}
      </Head>
      <StyledLayout>
        <Header />
        <BackBreadcrumb
          title={activity.name()}
          href={widgetRoutes.CATEGORY.url({
            widgetSlug,
            categoryId: categorySlug
          })}
        />
        {/**
         *
         * Hero
         *
         */}
        <HeroSection>
          <CoverImage
            src={activity.imageURL('fullscreen')}
            height={190}
            variant="hero"
            top={
              bookingFromActivityVoucher ? (
                <Tag variant="icon">
                  <Icon name="voucher" color="gold" size={20} mr={3} />
                  <H6>{t('common:included-in-voucher')}</H6>
                </Tag>
              ) : (
                activity.members_only && (
                  <Tag variant="icon">
                    <Icon name="voucher" color="gold" size={20} mr={3} />
                    <H6>{t('common:members-only')}</H6>
                  </Tag>
                )
              )
            }
          />
          <HeroSectionContainer>
            <HeroSectionContainerRow>
              <Text fontStyle="h3" as="h1">
                {activity.name()}
              </Text>
            </HeroSectionContainerRow>
            <HeroSectionContainerRow>
              <TagsList
                tags={[
                  definedNotNull(activity?.activity_type?.name),
                  activity.formatDurationText(t),
                  t('maximum-people', {
                    count: definedNotNull(activity.maximum_capacity)
                  })
                ]}
              />
            </HeroSectionContainerRow>
            <DetailsList>
              <DetailsListItem>
                <Icon name="house-outline" size={24} mr={3} />
                <Text fontStyle="h6">
                  <Link
                    href={widgetRoutes.OUTLET.url({
                      outletSlug: definedNotNull(outlet.slug)
                    })}
                  >
                    {outlet.name}
                  </Link>
                </Text>
              </DetailsListItem>

              <DetailsListItem
                showChevron={!noCancellationPolicy}
                onClick={() => {
                  if (!noCancellationPolicy) {
                    goToCancellationPolicy()
                  }
                }}
              >
                <Icon name="calendar-edit" size={24} mr={3} />
                <Text fontStyle="h6">{outlet.cancellationPolicyShort()}</Text>
              </DetailsListItem>

              {outlet.widget_show_reviews &&
                outlet.ratings &&
                outlet.ratings[0] && (
                  <DetailsListItem
                    showChevron={true}
                    onClick={() => {
                      push('#reviews')
                    }}
                  >
                    <Icon name="script" size={24} mr={3} />
                    <RowFlex>
                      <ReviewText>({outlet.ratings[0].score})</ReviewText>
                      <Rating
                        score={definedNotNull(outlet.ratings[0].score)}
                        maxScore={5}
                      />
                      {numberOfReviews > 5 && (
                        <Text ml={1} fontStyle="h6">
                          {t('reviews', {
                            count: outlet.ratings[0].number_of_ratings
                          })}
                        </Text>
                      )}
                    </RowFlex>
                  </DetailsListItem>
                )}
              {activity.addressSummaryLine() && (
                <DetailsListItem
                  expansion={
                    <>
                      <Text color="tertiary">
                        {[
                          activity.address?.building_name,
                          activity.address?.city,
                          activity.address?.post_code,
                          activity.address?.country
                        ]
                          .filter(Boolean)
                          .map(line => (
                            <div key={line}>{line}</div>
                          ))}
                      </Text>
                      {activity.googleMapsURL() && (
                        <>
                          <br />
                          <a href={activity.googleMapsURL()}>
                            <Image
                              alt="Location on map"
                              src={activity.googleMapsImageURL() || ''}
                              width={320}
                              height={240}
                            />
                          </a>
                        </>
                      )}
                    </>
                  }
                >
                  <Icon name="pin" size={24} mr={3} />
                  <Text fontStyle="h6">{activity.addressSummaryLine()}</Text>
                </DetailsListItem>
              )}
              {activity.isMultiDay() && (
                <DetailsListItem>
                  <Icon name="calendar" size={24} mr={3} />
                  <Text fontStyle="h6">
                    {activity.continuous
                      ? t('this-is-a-x-night-continuous-activity', {
                          count: activity.getDurationInNights()
                        })
                      : t('this-is-a-x-day-activity', {
                          count: activity.getDurationInDays()
                        })}
                  </Text>
                </DetailsListItem>
              )}
              {activity.requiresApproval() && (
                <DetailsListItem>
                  <Icon name="tick-circle" size={24} mr={3} />
                  <Text fontStyle="h6">{t('approval-required')}</Text>
                </DetailsListItem>
              )}
              {activity.adultsAllowed() &&
                typeof activity.price_pence === 'number' && (
                  <DetailsListItem>
                    <Icon name="ticket-price" size={24} mr={3} />
                    <PriceWrapper color="secondary">
                      <PriceText color="secondary">From </PriceText>
                      <Price
                        pence={activity.price_pence}
                        currency={outlet.currency()}
                      />
                      <PriceText color="secondary"> / adult</PriceText>
                    </PriceWrapper>
                  </DetailsListItem>
                )}
              {activity.childrenAllowed() &&
                typeof activity.child_price_pence === 'number' && (
                  <DetailsListItem>
                    <Icon name="ticket-price" size={24} mr={3} />
                    <PriceWrapper color="secondary">
                      <PriceText color="secondary">From </PriceText>
                      <Price
                        pence={activity.child_price_pence}
                        currency={outlet.currency()}
                      />
                      <PriceText color="secondary"> / child</PriceText>
                    </PriceWrapper>
                  </DetailsListItem>
                )}
              {activity.groupDiscountsAvailable() && (
                <DetailsListItem>
                  <Icon name="user-group" size={24} mr={3} />
                  <Text fontStyle="h6">{t('group-discount')} </Text>
                </DetailsListItem>
              )}
            </DetailsList>
          </HeroSectionContainer>
        </HeroSection>
        {/**
         *
         * Main
         *
         */}
        {activity.hasDetails() && (
          <>
            <main>
              <ItemUl>
                <Item>
                  {activity.tagline() && (
                    <ItemText>
                      <StyledTagline color="secondary">
                        {activity.tagline()}
                      </StyledTagline>
                    </ItemText>
                  )}
                  {activity.description() && (
                    <ItemText>
                      <H5>{t('description')}</H5>
                      <Text
                        as="div"
                        color="secondary"
                        dangerouslySetInnerHTML={{
                          __html: activity.description()
                        }}
                      />
                    </ItemText>
                  )}
                  {activity.requirements() && (
                    <ItemText>
                      <H5>{t('requirements')}</H5>
                      <Text
                        as="div"
                        color="secondary"
                        dangerouslySetInnerHTML={{
                          __html: activity.requirements()
                        }}
                      />
                    </ItemText>
                  )}
                  {activity.whatToBring() && (
                    <ItemText>
                      <H5>{t('what-to-bring')}</H5>
                      <Text
                        as="div"
                        color="secondary"
                        dangerouslySetInnerHTML={{
                          __html: activity.whatToBring()
                        }}
                      />
                    </ItemText>
                  )}
                  {activity.itinerary() && (
                    <ItemText>
                      <H5>{t('itinerary')}</H5>
                      <Text
                        as="div"
                        color="secondary"
                        dangerouslySetInnerHTML={{
                          __html: activity.itinerary()
                        }}
                      />
                    </ItemText>
                  )}
                </Item>
              </ItemUl>

              <ShowOnMarketplace>
                <ItemUl>
                  {outlet.description() && (
                    <Item mt={3}>
                      <ItemText>
                        <H5>
                          {t('common:about-outlet', { name: outlet.name })}
                        </H5>
                        <Text
                          as="div"
                          color="secondary"
                          dangerouslySetInnerHTML={{
                            __html: outlet.description()
                          }}
                        />
                      </ItemText>
                    </Item>
                  )}
                  <Item mt={3}>
                    <ItemText>
                      <H5>{t('common:about-outlet', { name: 'eola' })}</H5>
                      <Text color="secondary">
                        {t('common:about-eola-body')}
                      </Text>
                    </ItemText>
                  </Item>
                </ItemUl>
              </ShowOnMarketplace>
            </main>
            {outlet.widget_show_reviews &&
              outlet.ratings &&
              outlet.ratings[0] &&
              outlet.reviews && (
                <ReviewsSection>
                  <Section>
                    <H4>{t('top-reviews')}</H4>
                  </Section>
                  <ReviewsCarousel
                    reviews={outlet.reviews}
                    rating={definedNotNull(outlet.ratings[0].score)}
                    allReviewsUrl={defined(outlet.ratings[0].url)}
                    totalNumberOfReviews={numberOfReviews}
                    centeredSlides={true}
                  />
                </ReviewsSection>
              )}

            <PoweredByEola />
          </>
        )}

        {/**
         *
         * BottomBar
         *
         */}
        <BottomBar padding={0}>
          <VoucherBar
            activity={{ isActive: !!bookingFromActivityVoucher }}
            credit={{ data: creditVoucher }}
            discount={{ data: discountCode }}
            onClick={() => setActiveModalId(MODAL_IDS.activeVoucher)}
          />
          <BottomBarContainer>
            <BottomBarButton
              onClick={
                requestsEnabledAndNoAvailability
                  ? goToRequest
                  : bookSchedulableOnClick
              }
              loading={!!loading}
            >
              {requestsEnabledAndNoAvailability
                ? t('request-a-time')
                : bookingFromActivityVoucher
                  ? t('book-activity')
                  : t('common:book-now')}
            </BottomBarButton>
          </BottomBarContainer>
        </BottomBar>
        <ModalsSchedulableDetails
          activeModal={activeModal}
          {...currentModalProps}
          setActiveModalId={setActiveModalId}
        />
      </StyledLayout>
    </>
  )
}

export default ActivityDetails

const FETCH_CALENDAR_AVAILABILITY_AGGREGATE = gql`
  query WidgetActivityDetailsFetchCalendarAvailabilityAggregate(
    $schedulableId: uuid
  ) {
    materialized_bookability_by_days_aggregate(
      where: {
        schedulable: { id: { _eq: $schedulableId } }
        max_availability: { _gte: 1 }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

const RowFlex = styled(Flex)(() =>
  css({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px'
  })
)

const ReviewsSection = styled.div(({ theme }) => ({
  padding: `0 ${theme.space[4]}px`,

  [theme.mediaQueries.md]: {
    padding: `0`
  }
}))

const ReviewText = styled(Text)(() =>
  css({
    lineHeight: '1rem'
  })
)

const StyledLayout = styled(Layout)(({ theme }) => ({
  background: theme.colors.white,
  main: {
    paddingTop: 0
  }
}))

const Section = styled.div(({ theme }) => ({
  ...theme.mixins.centeredContainer(theme.currentMaxWidth, 0)
}))

const StyledTagline = styled(Text)(({ theme }) => ({
  ...theme.mixins.ellipsisLineClamp(3)
}))
